import type { AxiosError, AxiosInstance } from 'axios';
import { captureException, captureMessage, isEnabled } from '@pleased-npm/error-tracking';
import { isExpectedBusinessError, isExpectedBusinessErrorCode } from '@/common/error';
import { ErrorTrackingEvent } from './enums';
import { getResponseBusinessCode, isAxiosErrorLike, isCancelledRequest } from './guards';
import type { ApiFailurePayload, CaptureEventContext, NormalizedCapture } from './types';
import { isPollingUrl, normalizeUrlForTracking, sanitizeUrlForTracking } from './url';

/**
 * Keep the frames of the original throw so grouping and the culprit stay accurate while the
 * title says what actually failed.
 */
const withStackOf = (message: string, source: unknown): Error => {
  const error = new Error(message);
  const stack = (source as { stack?: unknown } | null)?.stack;
  if (typeof stack === 'string') {
    error.stack = stack;
  }
  return error;
};

/** Describe a thrown non-error without copying a response body that may hold visitor data. */
const describeNonError = (value: unknown): string => {
  if (value === null) return 'null';
  if (typeof value !== 'object') return String(value);
  const keys = Object.keys(value as object);
  return keys.length ? `object with keys ${keys.join(', ')}` : 'empty object';
};

/** BE appends a per-request correlation token — must not drive GlitchTip exception grouping. */
const BE_CORRELATION_CODE_SUFFIX = /\s+Code:\s+[A-Z0-9]+$/i;

const stripBeCorrelationCode = (message: string): { normalized: string; correlationCode?: string } => {
  const match = BE_CORRELATION_CODE_SUFFIX.exec(message);
  if (!match) return { normalized: message };

  const correlationCode = match[0].trim().replace(/^Code:\s*/i, '');
  return {
    normalized: message.replace(BE_CORRELATION_CODE_SUFFIX, '').trimEnd(),
    correlationCode,
  };
};

/** Pleased numeric `code` or Sporty `bizCode` on a throw — not Node/Axios string `.code`. */
const getThrownBusinessCode = (error: Error): string | undefined => {
  const { code, bizCode } = error as { code?: unknown; bizCode?: unknown };
  if (typeof code === 'number' && Number.isFinite(code)) {
    return String(code);
  }
  if (typeof bizCode === 'number' && Number.isFinite(bizCode)) {
    return String(bizCode);
  }
  if (typeof bizCode === 'string' && bizCode.length) {
    return bizCode;
  }
  return undefined;
};

/**
 * `throwIfError` throws `Error` with the API `message` (often including a unique correlation suffix)
 * and Pleased `code` / Sporty `bizCode`. Strip the suffix for grouping; fingerprint by business code.
 */
const normalizePlainError = (error: Error): NormalizedCapture => {
  const apiCode = getThrownBusinessCode(error);
  const { normalized, correlationCode } = stripBeCorrelationCode(error.message);
  const messageChanged = normalized !== error.message;

  if (!messageChanged && apiCode == null) {
    return { error };
  }

  const extra: Record<string, unknown> = {};
  if (messageChanged) {
    extra.originalMessage = error.message;
    if (correlationCode) {
      extra.correlation_code = correlationCode;
    }
  }

  let fingerprintKey: string | undefined;
  if (apiCode != null) {
    fingerprintKey = apiCode;
  } else if (messageChanged) {
    fingerprintKey = normalized;
  }

  return {
    error: messageChanged ? withStackOf(normalized, error) : error,
    tags: apiCode != null ? { api_code: apiCode } : undefined,
    extra: Object.keys(extra).length ? extra : undefined,
    fingerprintKey,
  };
};

/**
 * An axios error keeps the endpoint in `config`, so its message is only
 * `Request failed with status code 400`. Name the request in the title instead.
 */
const normalizeAxiosError = (error: AxiosError): NormalizedCapture => {
  const method = error.config?.method?.toUpperCase();
  const status = error.response?.status;
  const businessCode = getResponseBusinessCode(error.response?.data);
  const transportCode = error.response ? undefined : error.code;
  const groupedUrl = normalizeUrlForTracking(error.config?.url);
  const outcome = status != null ? String(status) : transportCode;
  const description = ['API request failed', outcome, method, groupedUrl].filter(Boolean).join(' ');

  return {
    error: withStackOf(businessCode != null ? `${description} (code ${businessCode})` : description, error),
    tags: {
      api_status: status != null ? String(status) : undefined,
      api_code: businessCode != null ? String(businessCode) : undefined,
      api_transport_code: transportCode,
      api_method: method,
      api_url: groupedUrl,
    },
    extra: {
      url: sanitizeUrlForTracking(error.config?.url),
      method,
      status,
      code: businessCode,
      transportCode,
      message: error.message,
    },
  };
};

/** `throwIfError(response, true)` rejects with the response body, which is not an `Error`. */
const normalizeApiBodyError = (error: unknown): NormalizedCapture | undefined => {
  const businessCode = getResponseBusinessCode(error);
  const responseMessage = (error as { message?: unknown } | null)?.message;
  if (businessCode == null && typeof responseMessage !== 'string') return undefined;

  const strippedMessage =
    typeof responseMessage === 'string' ? stripBeCorrelationCode(responseMessage) : undefined;

  const description = [
    businessCode != null ? `API error ${businessCode}` : 'API error',
    strippedMessage?.normalized,
  ]
    .filter(Boolean)
    .join(': ');

  const extra: Record<string, unknown> = {};
  if (typeof responseMessage === 'string' && strippedMessage?.normalized !== responseMessage) {
    extra.originalMessage = responseMessage;
    if (strippedMessage?.correlationCode) {
      extra.correlation_code = strippedMessage.correlationCode;
    }
  }

  return {
    error: new Error(description),
    tags: { api_code: businessCode != null ? String(businessCode) : undefined },
    extra: Object.keys(extra).length ? extra : undefined,
    fingerprintKey: businessCode != null ? String(businessCode) : strippedMessage?.normalized,
  };
};

/**
 * Rebuild throws that carry no usable title of their own — axios errors, rejected API bodies and
 * bare values — into an error that names the failure. Plain `Error` values from `throwIfError`
 * (and similar) are normalized to strip BE correlation suffixes and attach business-code tags.
 */
export const normalizeCapturedError = (error: unknown): NormalizedCapture => {
  if (isAxiosErrorLike(error)) return normalizeAxiosError(error as AxiosError);
  if (error instanceof Error) return normalizePlainError(error);
  if (typeof error === 'string') return { error: new Error(error) };

  return normalizeApiBodyError(error) ?? { error: new Error(`Non-error thrown: ${describeNonError(error)}`) };
};

/**
 * Errors already sent with an `event` tag. Outer flow-level wrappers (`ChatbotFlowAction`,
 * `ConnectRequest`) catch what inner handlers miss without duplicating what they reported.
 */
const reportedErrors = new WeakSet<object>();

/** Predictive suggestion captures carry endpoint/id tags — keep them in the fingerprint. */
const buildExceptionFingerprint = (
  event: string,
  context: CaptureEventContext | undefined,
  normalized: NormalizedCapture
): string[] | undefined => {
  const apiCode = context?.tags?.api_code ?? normalized.tags?.api_code ?? normalized.fingerprintKey;

  if (event === ErrorTrackingEvent.PredictiveSuggestion) {
    const predictionId = context?.tags?.prediction_id;
    const sbEndpoint = context?.tags?.sb_endpoint;
    if (predictionId || sbEndpoint) {
      return [event, predictionId ?? 'unknown', sbEndpoint ?? 'unknown', apiCode ?? 'unknown'];
    }
  }

  if (apiCode) {
    return [event, apiCode];
  }

  if (normalized.fingerprintKey && event !== ErrorTrackingEvent.PredictiveSuggestion) {
    return [event, normalized.fingerprintKey];
  }

  return undefined;
};

/**
 * Capture an event-tagged exception, skipping cancelled / aborted requests (parity with axios interceptor),
 * expected business-rule limits (request / threshold limits handled by dedicated UX) and errors already
 * reported with a more specific event.
 *
 * Optional `context` merges caller tags/extra (e.g. prediction id, Sporty `bizCode`) on top of
 * anything derived by `normalizeCapturedError`.
 */
export const captureEventException = (
  error: unknown,
  event: ErrorTrackingEvent | string,
  context?: CaptureEventContext
): void => {
  if (!isEnabled()) return;
  if (isCancelledRequest(error)) return;
  if (isExpectedBusinessError(error)) return;
  if (isExpectedBusinessErrorCode(getResponseBusinessCode(error))) return;
  // A limit served as a non-2xx keeps its code in the response body, not on the error itself.
  if (isExpectedBusinessErrorCode(getResponseBusinessCode((error as AxiosError | null)?.response?.data))) return;

  if (error !== null && typeof error === 'object') {
    if (reportedErrors.has(error)) return;
    reportedErrors.add(error);
  }

  const normalized = normalizeCapturedError(error);

  const tags: Record<string, string | undefined> = {
    ...normalized.tags,
    ...context?.tags,
    event,
  };

  const extra: Record<string, unknown> = {
    ...normalized.extra,
    ...context?.extra,
  };

  const fingerprint = buildExceptionFingerprint(event, context, normalized);
  const options: {
    tags: Record<string, string | undefined>;
    extra?: Record<string, unknown>;
    fingerprint?: string[];
  } = { tags };
  if (Object.keys(extra).length) {
    options.extra = extra;
  }
  if (fingerprint) {
    options.fingerprint = fingerprint;
  }

  captureException(normalized.error, options);
};

/**
 * Message events carry no exception metadata, so the issue list shows the message and nothing
 * else. Spell out endpoint and outcome there instead of the bare `API Error`.
 */
const buildApiFailureMessage = (event: string, payload: ApiFailurePayload, url?: string): string => {
  const outcome = payload.status != null ? String(payload.status) : payload.transportCode;
  const description = [event, outcome, payload.method?.toUpperCase(), url].filter(Boolean).join(' ');
  return payload.code != null ? `${description} (code ${payload.code})` : description;
};

export const captureApiFailure = (payload: ApiFailurePayload): void => {
  if (!isEnabled()) return;
  if (isPollingUrl(payload.url)) return;
  if (isExpectedBusinessErrorCode(payload.code)) return;

  const event = payload.event || 'API Error';
  const method = payload.method?.toUpperCase();
  const safeUrl = sanitizeUrlForTracking(payload.url);
  const groupedUrl = normalizeUrlForTracking(payload.url);

  captureMessage(buildApiFailureMessage(event, payload, groupedUrl), 'error', {
    // Without an explicit fingerprint every endpoint and status collapses into a single issue,
    // because the default grouping of a message event is the message text alone.
    // Status/transport and business code are separate so HTTP 200 + different `code`
    // values (throwIfError path) do not collapse into one issue.
    fingerprint: [
      event,
      method ?? 'unknown',
      groupedUrl ?? 'unknown',
      String(payload.status ?? payload.transportCode ?? 'unknown'),
      String(payload.code ?? 'unknown'),
    ],
    tags: {
      api_status: payload.status != null ? String(payload.status) : undefined,
      api_code: payload.code != null ? String(payload.code) : undefined,
      api_transport_code: payload.transportCode,
      api_method: method,
      api_url: groupedUrl,
    },
    extra: {
      url: safeUrl,
      method,
      status: payload.status,
      code: payload.code,
      transportCode: payload.transportCode,
      reason: payload.reason,
    },
  });
};

type AxiosRequestConfigWithTracking = {
  skipErrorTracking?: boolean;
  url?: string;
  method?: string;
};

/**
 * Attach shared response-error reporting to any Axios instance
 * (httpClient, legacy Sporty/Zendesk Api, vendor PleasedChatClient).
 * Skips polling URLs, cancelled / aborted requests, and `skipErrorTracking` configs
 * (used when a caller reports via `captureEventException` instead).
 *
 * Reports the same business code as `throwIfError`, so a limit response that arrives as a
 * non-2xx is filtered here too instead of landing in GlitchTip as an incident.
 */
export const attachAxiosErrorTracking = (client: AxiosInstance): void => {
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const config = error.config as AxiosRequestConfigWithTracking | undefined;
      if (!config?.skipErrorTracking && !isCancelledRequest(error) && !isPollingUrl(config?.url ?? error.config?.url)) {
        captureApiFailure({
          event: 'API Network Error',
          url: config?.url ?? error.config?.url,
          method: config?.method ?? error.config?.method,
          status: error.response?.status,
          code: getResponseBusinessCode(error.response?.data),
          // Without a response there is no status either — the transport code is all that separates
          // a timeout from a DNS/CORS failure.
          transportCode: error.response ? undefined : error.code,
        });
      }
      return Promise.reject(error);
    }
  );
};

/** Staging/prod smoke helper — call once to verify ingest. */
export const captureSmokeTestError = (): void => {
  if (!isEnabled()) return;
  captureException(new Error('[error-tracking] smoke test'));
};
